import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import blueLogo from '../assets/bluelogo.png';
import './Login.css';

// Home's nine roles (students/home.py HOME_ROLES) land on it after signing in.
const HOME_ROLES = ['CHAIRMAN', 'CFO', 'ADMIN', 'CEO', 'COO', 'CSM', 'CMO', 'PERMANENT_EMPLOYEE', 'SUPPORT'];

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const loginSuccessRef = useRef(false);

  useEffect(() => {
    // Redirect if already authenticated - this will handle redirect after login
    if (isAuthenticated && user && (loginSuccessRef.current || localStorage.getItem('token'))) {
      loginSuccessRef.current = false; // Reset flag
      const userRole = user?.role?.toUpperCase();
      // Use requestAnimationFrame to ensure DOM and state are synchronized
      requestAnimationFrame(() => {
        if (HOME_ROLES.includes(userRole) || user?.is_superuser) {
          navigate('/dashboard', { replace: true });
        } else if (userRole === 'COACH') {
          navigate('/my-analytics', { replace: true });
        } else {
          navigate('/students', { replace: true });
        }
      });
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(username, password, mfaCode);

      if (result.mfaRequired) {
        setMfaRequired(true);
        setError('');
        setLoading(false);
        return;
      }

      if (result.success) {
        // Use hard redirect to ensure ProtectedRoute sees the updated auth state
        const user = result.user || JSON.parse(localStorage.getItem('user') || '{}');
        const userRole = user?.role?.toUpperCase();

        // Small delay to ensure localStorage is saved, then hard redirect
        setTimeout(() => {
          if (HOME_ROLES.includes(userRole) || user?.is_superuser) {
            window.location.href = '/dashboard';
          } else if (userRole === 'COACH') {
            window.location.href = '/my-analytics';
          } else {
            window.location.href = '/students';
          }
        }, 100);
      } else {
        setError(result.error || 'Login failed');
        setLoading(false);
      }
    } catch (err) {
      setError('An error occurred during login');
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src={blueLogo} alt="VisionExploit" style={{ height: 52, marginBottom: 14, objectFit: 'contain' }} />
          <p>Sign in to your account</p>
        </div>
        
        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {mfaRequired && (
            <div className="form-group">
              <label htmlFor="mfaCode">Authentication code</label>
              <input
                type="text"
                id="mfaCode"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="6-digit code from your authenticator"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                autoFocus
              />
            </div>
          )}

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Signing in...' : (mfaRequired ? 'Verify & sign in' : 'Sign In')}
          </button>

          <p className="login-links">
            <a
              href={process.env.REACT_APP_WEBSITE_ADMIN_URL || 'http://localhost:3001/admin-portal'}
              target="_blank"
              rel="noopener noreferrer"
            >
              Website Admin →
            </a>
            <span className="login-links-sep"> · </span>
            <a href="/student-app-admin">Student App Admin →</a>
          </p>
        </form>
      </div>
    </div>
  );
};

export default Login;

